import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../../RoutesWithAuth';
import axios from '../../../../common/AxiosConfig';
import { makeStyles } from '@mui/styles';
import { Button, FormControl, IconButton, InputAdornment, MenuItem, Select, TextField } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';

const FIELD_OPTIONS = [
    'Assigned To',
    'Due Date',
    'First and Last Name',
    'Priority',
    'Status',
    'Workflow Stage'
];

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];
const STATUS_OPTIONS = ['In progress', 'Hold', 'Cancelled', 'Done'];
const WORKFLOW_STAGE_OPTIONS = ['New', 'CXD Privacy', 'P&G Privacy', 'Legal', 'Completed'];
const FIELD_KEYS = {
    'Assigned To': 'assigned_to',
    'Due Date': 'due_date',
    'First and Last Name': 'poc_name',
    Priority: 'priority',
    Status: 'status',
    'Workflow Stage': 'workflow_stage'
};

const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const useStyles = makeStyles(() => ({
    header: {
        fontWeight: '600',
        backgroundColor: '#62A9D4',
        height: '5vh',
        padding: '0 8px 0 17px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    closeButton: {
        color: '#202124',
        padding: '4px'
    },
    content: {
        padding: '24px 20px 0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    fieldRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '470px',
        marginBottom: '16px',
        marginTop: '15px'
    },
    reasonRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        width: '470px'
    },
    dynamicRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        width: '470px',
        marginBottom: '16px'
    },
    label: {
        fontWeight: 600,
        fontSize: '0.85rem',
        whiteSpace: 'nowrap',
        width: '125px',
        paddingTop: '8px'
    },
    required: {
        color: '#d32f2f'
    },
    selectControl: {
        width: '260px'
    },
    selectPlaceholder: {
        color: 'rgba(0, 0, 0, 0.42)'
    },
    textArea: {
        width: '260px',
        '& .MuiInputBase-input': {
            fontSize: '0.85rem',
            resize: 'none'
        }
    },
    dateInputWrapper: {
        position: 'relative',
        width: '260px'
    },
    datePlaceholder: {
        position: 'absolute',
        top: '50%',
        left: '11px',
        zIndex: 1,
        right: '48px',
        transform: 'translateY(-50%)',
        backgroundColor: '#FFFFFF',
        color: 'rgba(0, 0, 0, 0.6)',
        fontSize: '0.8rem',
        pointerEvents: 'none'
    },
    dateInput: {
        width: '260px',
        '& input[type="date"]::-webkit-calendar-picker-indicator': {
            opacity: 0
        }
    },
    actions: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '40px',
        padding: '16px 20px 0'
    },
    cancelButton: {
        backgroundColor: '#3924FC',
        color: '#FFFFFF'
    },
    submitButton: {
        backgroundColor: '#3924FC',
        color: '#FFFFFF'
    },
    errorMessage: {
        color: '#d32f2f',
        fontSize: '0.8rem',
        textAlign: 'center',
        marginTop: '8px'
    }
}));

const UpdateTaskInformation = ({ taskId, onClose, onUpdateSuccess }) => {
    const classes = useStyles();
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const [users, setUsers] = useState([]);
    const [selectedField, setSelectedField] = useState('');
    const [fieldValue, setFieldValue] = useState('');
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        axios.get('users', {
            headers: {
                Authorization: authToken
            }
        }).then(res => {
            if (res.data.length) {
                const filteredUsers = res.data.filter(user => user.user_status === 'Y' && !user.is_disabled);
                setUsers(filteredUsers);
            }
        }).catch(err => {
            console.log('There was an error fetching users!', err);
        });
    }, [authToken]);

    const handleSelectedFieldChange = (event) => {
        setSelectedField(event.target.value);
        setFieldValue('');
        setSubmitError('');
    };

    const handleNameChange = (event) => {
        const value = event.target.value.trimStart().replace(/\s+/g, ' ');

        if (/^[A-Za-z\s]*$/.test(value)) {
            setFieldValue(value);
        }
    };

    const handleReasonChange = (event) => {
        const value = event.target.value;

        if (/^[a-zA-Z0-9,.\s\-()]*$/.test(value)) {
            setReason(value);
        }
    };

    const handleCancel = () => {
        setSelectedField('');
        setFieldValue('');
        setReason('');
        setSubmitError('');
    };

    const handleSubmit = () => {
        if (!taskId || !selectedField || !fieldValue || !reason.trim()) {
            setSubmitError('Please complete all mandatory fields.');
            return;
        }

        const selectedUser = selectedField === 'Assigned To'
            ? users.find((user) => user.user_id === fieldValue)
            : null;
        if (selectedField === 'Assigned To' && !selectedUser) {
            setSubmitError('Please select a valid user.');
            return;
        }

        setIsSubmitting(true);
        setSubmitError('');
        axios.put(`centra/privacy-task/${encodeURIComponent(taskId)}`, {
            field: FIELD_KEYS[selectedField] || selectedField,
            new_value: selectedUser
                ? `${selectedUser.first_name} ${selectedUser.last_name}`.trim()
                : typeof fieldValue === 'string' ? fieldValue.trim() : fieldValue,
            reason: reason.trim()
        }, {
            headers: {
                Authorization: authToken,
                'Content-Type': 'application/json'
            }
        }).then((res) => {
            onUpdateSuccess?.(res.data);
            onClose?.();
        }).catch((err) => {
            setSubmitError(err?.response?.data?.message || err?.message || 'Unable to update task information.');
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    const renderSelectField = (options, placeholder) => (
        <FormControl className={classes.selectControl} size="small">
            <Select
                displayEmpty
                value={fieldValue}
                onChange={(event) => setFieldValue(event.target.value)}
                renderValue={(value) => value || <span className={classes.selectPlaceholder}>{placeholder}</span>}
            >
                <MenuItem disabled value="">{placeholder}</MenuItem>
                {options.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const renderSelectedFieldInput = () => {
        if (!selectedField) return null;

        let label = selectedField;
        let input = null;

        if (selectedField === 'Assigned To') {
            input = (
                <FormControl className={classes.selectControl} size="small">
                    <Select
                        displayEmpty
                        value={fieldValue}
                        onChange={(event) => setFieldValue(event.target.value)}
                        renderValue={(value) => {
                            if (!value) return <span className={classes.selectPlaceholder}>Select User</span>;
                            const selectedUser = users.find((user) => user.user_id === value);
                            return selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : value;
                        }}
                    >
                        <MenuItem disabled value="">Select User</MenuItem>
                        {users.map((user) => (
                            <MenuItem key={user.user_id} value={user.user_id}>{`${user.first_name} ${user.last_name}`}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        } else if (selectedField === 'First and Last Name') {
            input = (
                <TextField
                    className={classes.textArea}
                    size="small"
                    value={fieldValue}
                    onChange={handleNameChange}
                    onBlur={(event) => setFieldValue(event.target.value.trim())}
                    inputProps={{ maxLength: 200 }}
                    placeholder="Enter First and Last Name"
                    variant="outlined"
                />
            );
        } else if (selectedField === 'Priority') {
            input = renderSelectField(PRIORITY_OPTIONS, 'Select Priority');
        } else if (selectedField === 'Status') {
            input = renderSelectField(STATUS_OPTIONS, 'Select Status');
        } else if (selectedField === 'Due Date') {
            label = 'Due Date';
            input = (
                <div className={classes.dateInputWrapper}>
                    {!fieldValue && (
                        <span className={classes.datePlaceholder}>Select Date</span>
                    )}
                    <TextField
                        className={classes.dateInput}
                        size="small"
                        type="date"
                        value={fieldValue}
                        onChange={(event) => setFieldValue(event.target.value)}
                        variant="outlined"
                        inputProps={{
                            min: getTomorrowDate(),
                            onClick: (event) => { event.currentTarget.showPicker?.(); }
                        }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        aria-label="Select Due Date"
                                        onClick={() => { document.getElementById('update-task-due-date')?.showPicker?.(); }}
                                    >
                                        <CalendarTodayIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        id="update-task-due-date"
                    />
                </div>
            );
        } else if (selectedField === 'Workflow Stage') {
            input = renderSelectField(WORKFLOW_STAGE_OPTIONS, 'Select Workflow Stage');
        }

        return (
            <div className={classes.dynamicRow}>
                <span className={classes.label}>{label}<span className={classes.required}>*</span>:</span>
                {input}
            </div>
        );
    };

    return (
        <div>
            <div className={classes.header}>
                <span>Update Task Information</span>
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose} size="small">
                    <CloseIcon fontSize="small" />
                </IconButton>
            </div>
            <div className={classes.content}>
                <div className={classes.fieldRow}>
                    <span className={classes.label}>Select Field<span className={classes.required}>*</span>:</span>
                    <FormControl className={classes.selectControl} size="small">
                        <Select
                            displayEmpty
                            value={selectedField}
                            onChange={handleSelectedFieldChange}
                            renderValue={(value) => value || <span className={classes.selectPlaceholder}>Select Field</span>}
                        >
                            <MenuItem disabled value="">Select Field</MenuItem>
                            {FIELD_OPTIONS.map((field) => (
                                <MenuItem key={field} value={field}>{field}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>
                {renderSelectedFieldInput()}
                <div className={classes.reasonRow}>
                    <span className={classes.label}>Update Reason<span className={classes.required}>*</span>:</span>
                    <TextField
                        className={classes.textArea}
                        multiline
                        rows={2}
                        value={reason}
                        onChange={handleReasonChange}
                        placeholder="Enter Update Reason/Comments"
                        variant="outlined"
                    />
                </div>
            </div>
            <div className={classes.actions}>
                <Button className = {classes.cancelButton} variant="contained" size="small" disabled={isSubmitting} onClick={handleCancel}>Cancel</Button>
                <Button className = {classes.submitButton} variant="contained" size="small" disabled={isSubmitting} onClick={handleSubmit}>Submit</Button>
            </div>
            {submitError && <div className={classes.errorMessage}>{submitError}</div>}
        </div>
    );
};

export default UpdateTaskInformation;